import { useMemo, useState } from "react";
import { Flame, Snowflake } from "lucide-react";
import PageHero from "@/components/bakery/PageHero";
import { OvenArt, ProductIcon } from "@/components/bakery/Art";
import { BatchForm, BakeForm } from "@/components/forms/ProductionForms";
import TxnDetailDialog from "@/components/common/TxnDetailDialog";
import { VoidBadge } from "@/components/common/Chips";
import { EmptyState, Loading, OutlineBtn, PrimaryBtn } from "@/components/common/ui";
import { inputCls } from "@/components/common/Field";
import { useApi } from "@/lib/useApi";
import { fmtDateTime, todayJkt } from "@/lib/format";
import { cn } from "@/lib/utils";

function TrayCard({ t, onOpen }) {
  const bake = t.subtype === "bake_fd";
  const total = t.lines.reduce((s, l) => s + l.qty, 0);
  return (
    <button onClick={() => onOpen(t.id)} data-testid={`tray-card-${t.txn_no}`}
      className={cn("lift press overflow-hidden rounded-xl border-2 border-ink bg-white text-left", t.status === "void" && "opacity-60")}>
      <div className="flex items-center justify-between bg-ink px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          {bake ? <Snowflake className="h-4 w-4 text-brand" /> : <Flame className="h-4 w-4 text-brand" />}
          <span className="font-display text-lg font-extrabold">{bake ? "Panggang FD" : `Batch ${t.batch_no}`}</span>
        </div>
        <VoidBadge txn={t} />
        <span className="num text-[11px] text-zinc-400">{t.txn_no}</span>
      </div>
      <div className="tray-surface m-3 rounded-lg border-[3px] border-zinc-300 p-3">
        <ul className="space-y-1.5">
          {t.lines.map((l, i) => (
            <li key={i} className="flex items-center gap-2 rounded-md bg-white/90 px-2 py-1.5 text-sm shadow-[0_1px_0_#e4e4e7]">
              <ProductIcon category={bake ? (l.output_category || l.category) : l.output === "fd" ? "Frozen Dough" : l.category} className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{bake ? (l.output_product_name || l.product_name) : l.product_name}</span>
              {bake ? <span className="shrink-0 truncate text-[10px] text-zinc-400">dari {l.product_name}</span> : l.output === "fd" && <span className="rounded bg-zinc-100 px-1.5 text-[10px] font-semibold">FD</span>}
              <span className="num font-bold">×{l.qty}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex justify-between px-4 pb-3 text-[11px] text-zinc-500">
        <span>PIC {t.pic_name} · {fmtDateTime(t.created_at)}</span><span className="num font-semibold text-ink">{total} pcs</span>
      </div>
    </button>
  );
}

function DailyTotals({ txns, pmap }) {
  const rows = useMemo(() => {
    const acc = {};
    (txns || []).filter((t) => t.status === "active").forEach((t) => t.lines.forEach((l) => {
      const pid = t.subtype === "bake_fd" ? (l.output_product_id || pmap[l.product_id]?.frozen_dough_of) : l.product_id;
      const r = (acc[pid] = acc[pid] || { pid, name: pmap[pid]?.name || "?", roti: 0, fd: 0, bake: 0 });
      r[t.subtype === "bake_fd" ? "bake" : l.output === "fd" ? "fd" : "roti"] += l.qty;
    }));
    return Object.values(acc);
  }, [txns, pmap]);
  if (!rows.length) return null;
  return (
    <div className="mb-8 overflow-x-auto rounded-xl border border-zinc-200 bg-white" data-testid="daily-production-totals">
      <table className="dense-table w-full">
        <thead><tr><th>Total per varian</th><th className="text-right">Roti Jadi</th><th className="text-right">Frozen Dough</th><th className="text-right">Panggang dari FD</th><th className="text-right">Total Roti</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.pid || r.name}><td className="font-medium">{r.name}</td><td className="num text-right">{r.roti}</td><td className="num text-right">{r.fd}</td><td className="num text-right">{r.bake}</td><td className="num text-right font-bold">{r.roti + r.bake}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function Dapur() {
  const [date, setDate] = useState(todayJkt());
  const [productFilter, setProductFilter] = useState("");
  const { data: txns, loading, reload } = useApi("/transactions", { type: "PRD", date_from: date, date_to: date, product_id: productFilter || undefined });
  const { data: products, reload: reloadP } = useApi("/products");
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const pmap = useMemo(() => Object.fromEntries((products || []).map((p) => [p.id, p])), [products]);
  const saved = () => { setForm(null); reload(); reloadP(); };
  return (
    <div data-testid="dapur-page">
      <PageHero testid="dapur-hero" eyebrow="Transaksi · Produksi" title="Dapur" Art={OvenArt}
        desc="Catat setiap batch oven sebagai loyang. Output bisa jadi roti siap jual atau Frozen Dough untuk dipanggang nanti."
        actions={<>
          <PrimaryBtn onClick={() => setForm("batch")} data-testid="open-batch-form"><Flame className="h-4 w-4" /> Batch Baru</PrimaryBtn>
          <OutlineBtn onClick={() => setForm("bake")} data-testid="open-bake-form"><Snowflake className="h-4 w-4" /> Panggang dari Frozen Dough</OutlineBtn>
        </>} />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={cn(inputCls, "sm:w-52")} data-testid="dapur-date-filter" />
        <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)} className={cn(inputCls, "sm:w-72")} data-testid="dapur-product-filter">
          <option value="">Semua produk</option>
          {(products || []).filter((p) => ["Roti", "Bagel", "Frozen Dough"].includes(p.category) && !p.is_bundle).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <DailyTotals txns={txns} pmap={pmap} />
      {loading && !txns ? <Loading /> : txns?.length ? (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="tray-list">{txns.map((t) => <TrayCard key={t.id} t={t} onOpen={setDetail} />)}</div>
      ) : (
        <EmptyState Art={OvenArt} title="Oven masih dingin" desc="Belum ada batch di tanggal ini. Mulai dengan Batch Baru." testid="dapur-empty" />
      )}
      <BatchForm open={form === "batch"} onClose={() => setForm(null)} products={products} defaultDate={date} onSaved={saved} />
      <BakeForm open={form === "bake"} onClose={() => setForm(null)} products={products} defaultDate={date} onSaved={saved} />
      <TxnDetailDialog txnId={detail} onClose={() => setDetail(null)} onChanged={reload} />
    </div>
  );
}
